import React from "react";
import { useNavigate } from "react-router-dom";
import { X, ShoppingBag, Lock } from "lucide-react";
import { useCart } from "../context/CartContext";
import { PRODUCT } from "../mock";

const money = (n) => `$${n.toFixed(2)}`;

const CartDrawer = () => {
  const { isOpen, setIsOpen, items, removeItem, subtotal, savings, count } = useCart();
  const navigate = useNavigate();

  const goCheckout = () => {
    setIsOpen(false);
    navigate("/checkout");
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-[70] bg-black/40 transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setIsOpen(false)}
      />
      <aside
        className={`fixed top-0 right-0 z-[80] h-full w-full max-w-[420px] bg-[#F4EFE8] shadow-2xl flex flex-col transition-transform duration-500 ease-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#1A1A1A]/10">
          <h3 className="text-[13px] tracking-[0.2em] uppercase">Your Cart ({count})</h3>
          <button onClick={() => setIsOpen(false)} className="hover:opacity-60">
            <X size={20} strokeWidth={1.4} />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8 gap-5">
            <ShoppingBag size={44} strokeWidth={1} className="opacity-30" />
            <p className="font-serif-display text-2xl">Your cart is empty</p>
            <button
              onClick={() => {
                setIsOpen(false);
                navigate(`/products/${PRODUCT.handle}`);
              }}
              className="text-[12px] tracking-[0.2em] uppercase underline underline-offset-4 hover:opacity-60"
            >
              Continue shopping
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto no-scrollbar px-6 py-5 space-y-6">
              {items.map((it) => (
                <div key={it.id} className="flex gap-4">
                  <img src={it.image} alt={it.title} className="w-24 h-28 object-cover rounded-sm bg-white" />
                  <div className="flex-1">
                    <p className="text-sm leading-snug pr-4">{it.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm font-medium">{money(it.price)}</span>
                      <span className="text-xs line-through opacity-40">{money(it.compareAt)}</span>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-[12px] tracking-[0.08em] uppercase text-[#1A1A1A]/55">
                        {(it.masks || it.qty)} {(it.masks || it.qty) > 1 ? "masks" : "mask"}
                      </span>
                      <button
                        onClick={() => removeItem(it.id)}
                        className="text-[11px] tracking-wider uppercase opacity-50 hover:opacity-90 underline underline-offset-2"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-[#1A1A1A]/10 px-6 py-5 space-y-4">
              {savings > 0 && (
                <div className="flex justify-between text-sm text-[#8a6d3b]">
                  <span>You're saving</span>
                  <span>{money(savings)}</span>
                </div>
              )}
              <div className="flex justify-between text-base">
                <span className="tracking-[0.12em] uppercase text-sm">Subtotal</span>
                <span className="font-medium">{money(subtotal)}</span>
              </div>
              <p className="text-[11px] opacity-50">Shipping & taxes calculated at checkout.</p>
              <button
                onClick={goCheckout}
                data-testid="drawer-checkout-btn"
                className="btn-lux w-full py-4 text-[12px] tracking-[0.22em] uppercase gap-2"
              >
                <Lock size={13} /> Checkout &bull; {money(subtotal)}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="w-full text-[11px] tracking-[0.2em] uppercase opacity-60 hover:opacity-100"
              >
                Continue shopping
              </button>
            </div>
          </>
        )}
      </aside>
    </>
  );
};

export default CartDrawer;
